/** 桌面客户端与服务端之间的本机能力契约；真实本机路径永不写入服务端。 */
export const DESKTOP_CAPABILITIES = [
  'files.read',
  'files.write',
  'terminal.exec',
  'browser.control',
  'screen.capture',
  'input.control',
  'local_mcp.call',
  'models.request',
] as const;

export type DesktopCapability = (typeof DESKTOP_CAPABILITIES)[number];
export type DesktopPlatform = 'win32' | 'darwin';
export interface DesktopBuiltinSkillManifest {
  id: 'browser' | 'web-access' | 'computer-control' | 'file-library' | 'file-download';
  version: string;
}
export const DESKTOP_RUNTIME_PROTOCOL_VERSION = 5;
/** 本机结果含命令输出或截图；客户端、网关和传输层使用同一上限。 */
export const DESKTOP_TOOL_RESULT_MAX_BYTES = 8 * 1024 * 1024;
export const LOCAL_AUTHORIZATION_PROFILES = ['read-only', 'on-request', 'full-access'] as const;
export type LocalAuthorizationProfile = (typeof LOCAL_AUTHORIZATION_PROFILES)[number];

export type ExecutionTargetPreference =
  | { mode: 'sandbox' }
  | {
      mode: 'desktop';
      deviceId: string;
      authorization: LocalAuthorizationProfile;
    };

export type ResolvedExecutionTarget =
  | { mode: 'sandbox' }
  | {
      mode: 'desktop';
      deviceId: string;
      bindingId: string;
      platform: DesktopPlatform;
      capabilities: DesktopCapability[];
      builtinSkills?: DesktopBuiltinSkillManifest[];
      authorization: LocalAuthorizationProfile;
    };
/** 服务端验证当前客户端连接后注入 Run；不接受模型自行声明。 */
export interface DesktopTaskContext {
  deviceId: string;
  authorization: LocalAuthorizationProfile;
  authSessionId: string;
}
export type DesktopToolRequestStatus =
  | 'pending'
  | 'awaiting_approval'
  | 'running'
  | 'completed'
  | 'rejected'
  | 'failed'
  | 'cancelled'
  | 'expired';
export type DesktopToolRisk = 'read' | 'write' | 'command' | 'high';

export interface DesktopDeviceView {
  scriptExecutionVersion?: number;
  taskDispatchVersion?: number;
  id: string;
  displayName: string;
  platform: DesktopPlatform;
  arch: string;
  appVersion: string;
  protocolVersion: number;
  capabilities: DesktopCapability[];
  builtinSkills?: DesktopBuiltinSkillManifest[];
  online: boolean;
  lastSeenAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DesktopWorkspaceBindingView {
  taskAuthorization?: LocalAuthorizationProfile | null;
  id: string;
  workspaceId: string;
  deviceId: string;
  displayName: string;
  capabilities: DesktopCapability[];
  isDefault: boolean;
  deviceOnline: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DesktopToolRequest {
  taskId?: string | null;
  id: string;
  workspaceId: string;
  deviceId: string;
  bindingId: string | null;
  runId: string;
  sessionId?: string;
  toolCallId: string;
  name: string;
  input: Record<string, unknown>;
  risk: DesktopToolRisk;
  authorization: LocalAuthorizationProfile;
  protocolVersion: number;
  expiresAt: string;
}

export interface DesktopToolResult {
  requestId: string;
  status: Extract<DesktopToolRequestStatus, 'completed' | 'rejected' | 'failed'>;
  data?: unknown;
  error?: string;
  outcome?: 'uncertain';
}

export interface DesktopToolResultAck {
  requestId: string;
  accepted: boolean;
  retryable?: boolean;
  error?: string;
}

/** 客户端准备开始真实操作时领取一次性资格；拒绝或超时都不能执行。 */
export interface DesktopToolClaimAck {
  requestId: string;
  accepted: boolean;
  error?: string;
}

export interface DesktopAuthResult {
  /** 当前账户在此客户端的设备标识；旧客户端响应可能省略。 */
  deviceId?: string;
  user: import('./auth').UserView;
  accessToken: string;
  refreshToken: string;
}

export interface DesktopUpdateState {
  revision: number;
  supported: boolean;
  currentVersion: string;
  latestVersion: string | null;
  phase:
    | 'disabled'
    | 'idle'
    | 'checking'
    | 'available'
    | 'downloading'
    | 'downloaded'
    | 'installing'
    | 'error';
  percent: number;
  message: string;
}

/** 系统授权与工作台授权分别核验；此状态只来自当前客户端主进程。 */
export interface DesktopComputerAccessState {
  supported: boolean;
  nativeReady: boolean;
  accessibility: boolean;
  screenCapture: boolean;
  restartRequired?: boolean;
  applicationPath?: string;
  message: string;
}

export interface DesktopBridge {
  computer?: {
    status(): Promise<DesktopComputerAccessState>;
    request(): Promise<DesktopComputerAccessState>;
    openSettings(permission: 'accessibility' | 'screenCapture'): Promise<void>;
  };
  links?: { open(url: string): Promise<void> };
  browser?: import('../desktop-browser').DesktopBrowserBridge & {
    onAnnotationSend?(
      listener: (
        input: import('../desktop-browser').BrowserAnnotationMessage,
      ) => Promise<import('../desktop-browser').BrowserAnnotationReceipt>,
    ): () => void;
  };
  models?: {
    authorize(userId: string, baseUrl: string): Promise<void>;
  };
  platform: DesktopPlatform;
  apiOrigin: string;
  deviceId: string;
  updates?: {
    status(): Promise<DesktopUpdateState>;
    check(): Promise<DesktopUpdateState>;
    download(): Promise<DesktopUpdateState>;
    install(): Promise<DesktopUpdateState>;
    onChange(listener: (state: DesktopUpdateState) => void): () => void;
  };
  window: {
    setPageMode(mode: 'auth' | 'app'): Promise<void>;
    minimize(): Promise<void>;
    toggleMaximize(): Promise<boolean>;
    isMaximized(): Promise<boolean>;
    close(): Promise<void>;
    onMaximizedChange(listener: (maximized: boolean) => void): () => void;
  };
  automation: {
    status(): Promise<{ paused: boolean }>;
    stop(): Promise<{ paused: boolean }>;
    resume(): Promise<{ paused: boolean }>;
  };
  auth: {
    login(input: {
      email: string;
      password: string;
      captchaPayload?: string;
    }): Promise<Omit<DesktopAuthResult, 'refreshToken'>>;
    refresh(): Promise<Omit<DesktopAuthResult, 'refreshToken'> | null>;
    logout(): Promise<void>;
  };
  directories: {
    pick(): Promise<{ path: string; displayName: string } | null>;
    bind(bindingId: string, path: string): Promise<void>;
    remove(bindingId: string): Promise<void>;
  };
}
